import React, { ChangeEvent, useEffect, useState } from 'react';
import { Button, Form, Offcanvas, OverlayTrigger, Popover } from 'react-bootstrap';
import DashBoardLeftNavBar from '@components/salesDashBoard/DashBoardLeftNavBar';
import loginUserIcon from '@assets/icon/user_icon.png';
import hamburgerIcon from '@assets/icon/hamburger_btn_icon.png';
import cctvIcon from '@assets/icon/cctv_icon(white).png';
import iotIcon from '@assets/icon/iot_icon(white).png';
import { useNavigate } from 'react-router-dom';
import { logoutRequest } from '@apis/signIn';
import { useRecoilState } from 'recoil';
import storeInfo from '../../recoil/storeInfo/atom';
import { AUTH } from '@dataTypes/authTypes';
import {
  changeStore,
  changeStoreByMaster,
  getMasterMainData,
  getUserMainData,
  renewStoreListsMaster,
  searchStoreListsByMaster,
  updateStoreMileageDisplay,
} from '@apis/salesDashboard';
import { IStoreResponseData, IStoreChangeData } from '@dataTypes/userInfo';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { SALESDASHBOARD } from './queryKey/queryKey';
import Cookies from 'js-cookie';
import DashBoardNav from './DashBoardNav';
import ExcelRead from '@components/perMachine/ExcelRead';
import { useCCTVList } from '@hooks/cctv/useCCTVList';
import { debounce } from 'lodash';
import { isMileageDisplayEnabled } from '@utils/mileageDisplay';
import StorePickButton from '@components/common/StorePickButton';
import { clearDemoSession, isDemoSession } from '@services/demo/demoAccount';
import { demoStoreState } from '@services/demo/demoPayloads';

interface DashBoardHeaderProps {
  displayNone?: boolean;
}

const DashBoardHeader = ({ displayNone }: DashBoardHeaderProps) => {
  const authority = Cookies.get('smartlab_authority');
  const navigation = useNavigate();
  const [show, setShow] = useState(false);
  const handleClose = () => setShow(false);
  const handleShow = () => setShow(true);
  const [shouldFetch, setShouldFetch] = useState(false);
  const [currentStoreInfo, setCurrentStoreInfo] = useRecoilState(storeInfo);
  const [fetchCCTVData, setFetchCCTVData] = useState(false);
  const { getCCTVListData, getCCTVListFetching, getCCTVListLoading } = useCCTVList(
    fetchCCTVData ? currentStoreInfo.currentStoreId : undefined,
  );
  const [searchStoreInputVal, setSearchStoreInputVal] = useState('');
  const [mileageToggleLoading, setMileageToggleLoading] = useState(false);
  const queryClient = useQueryClient();
  const cctvSeparationHandler = () => {
    setFetchCCTVData(true); // 버튼 클릭 시 요청을 트리거
  };

  useEffect(() => {
    if (fetchCCTVData && getCCTVListData && getCCTVListData.length > 0) {
      navigation('/admin/cctv');
    } else if (fetchCCTVData) {
      navigation('/admin/cctv/modify');
    }
  }, [fetchCCTVData, getCCTVListData]);

  const logoutHandler = async () => {
    const response = await logoutRequest();
    if (response) {
      clearDemoSession();
      setCurrentStoreInfo({
        currentStore: '',
        currentAuthority: '',
        currentStoreId: undefined,
        storeLists: [],
      });
      navigation('/signin');
    } else {
      console.error('logoutHandler error');
    }
  };

  useEffect(() => {
    if (isDemoSession()) {
      setCurrentStoreInfo(demoStoreState());
      setShouldFetch(false);
      return;
    }
    if (
      currentStoreInfo.currentStore === '' &&
      currentStoreInfo.currentAuthority === '' &&
      currentStoreInfo.currentStoreId === undefined &&
      currentStoreInfo.storeLists.length === 0
    ) {
      setShouldFetch(true);
    } else {
      setShouldFetch(false);
    }
  }, []);

  const {
    data: getCurrentListsData,
    isLoading: getInitDataLoading,
    isFetching: getInitDataFetching,
  } = useQuery<IStoreResponseData>(
    [SALESDASHBOARD.DASHBOARDCURRENTLISTS],
    () => separationAuthInit(),
    {
      enabled: shouldFetch,
    },
  );

  const separationAuthInit = () => {
    if (authority === AUTH.MASTER) {
      return getMasterMainData(); // 마스터 권한일 때 호출
    } else if (authority === AUTH.MANAGER || authority === AUTH.ADMIN) {
      return getUserMainData(); // 매니저·지점 관리자
    } else {
      return getUserMainData(); // 그 외 권한일 때 호출
    }
  };

  useEffect(() => {
    //마스터는 최초에 목록 최신화(지점 등록 상황)
    if (authority === AUTH.MASTER) {
      if (searchStoreInputVal === '') {
        renewalStoreListsHandler();
      } else {
        searchStoreListsByMasterHandler();
      }
    }
  }, [searchStoreInputVal]);

  useEffect(() => {
    if (getCurrentListsData && shouldFetch) {
      setCurrentStoreInfo({
        currentStore: getCurrentListsData.currentStore,
        currentAuthority: getCurrentListsData.currentAuthority,
        currentStoreId: getCurrentListsData.currentStoreId,
        storeLists: getCurrentListsData.storeLists,
      });
    }
  }, [getCurrentListsData, shouldFetch]);

  const changeIndexHandler = async (storeId: number) => {
    if (authority === AUTH.MASTER) {
      const response: IStoreChangeData = await changeStoreByMaster(storeId);
      if (response) {
        setCurrentStoreInfo((prevState) => ({
          ...prevState,
          currentStore: response.currentStore,
          currentAuthority: response.currentAuthority || '',
          currentStoreId: response.storeId,
        }));

        renewalStoreListsHandler();
      }
    } else {
      const response: IStoreChangeData = await changeStore(storeId);
      if (response) {
        setCurrentStoreInfo((prevState) => ({
          ...prevState,
          currentStore: response.currentStore,
          currentAuthority: response.currentAuthority || '',
          currentStoreId: response.storeId,
          storeLists:
            response.mileageDisplayYn != null && prevState.storeLists?.length
              ? prevState.storeLists.map((s) =>
                  s.storeId === response.storeId
                    ? { ...s, mileageDisplayYn: response.mileageDisplayYn }
                    : s,
                )
              : prevState.storeLists,
        }));
      }
    }

    // 검색 입력 값 초기화
    setSearchStoreInputVal('');
  };

  const renewalStoreListsHandler = async () => {
    const renewStoreListsMasterResult = await renewStoreListsMaster();
    if (renewStoreListsMasterResult) {
      setCurrentStoreInfo((prevState) => ({
        ...prevState,
        storeLists: renewStoreListsMasterResult.storeLists,
      }));
    } else {
      console.error('마스터 지점 목록 갱신 오류');
    }
  };

  const searchStoreListsByMasterHandler = async () => {
    const searchStoreListsByMasterResult = await searchStoreListsByMaster(searchStoreInputVal);
    if (searchStoreListsByMasterResult) {
      setCurrentStoreInfo((prevState) => ({
        ...prevState,
        storeLists: searchStoreListsByMasterResult.storeLists,
      }));
    }
  };

  const formattedAuth = () => {
    const auth = currentStoreInfo.currentAuthority;
    if (auth === AUTH.MASTER) {
      return '마스터';
    } else if (auth === AUTH.MANAGER) {
      return '매니저';
    } else if (auth === AUTH.ADMIN) {
      return '관리자';
    } else {
      return '유저';
    }
  };

  const searchStoreHandler = debounce((event: ChangeEvent<HTMLInputElement>) => {
    setSearchStoreInputVal(event.target.value);
  }, 500);

  const mileageVisibleForCurrent = isMileageDisplayEnabled(
    currentStoreInfo.currentStoreId,
    currentStoreInfo.storeLists,
  );

  const handleMileageDisplayToggle = async (checked: boolean) => {
    const sid = currentStoreInfo.currentStoreId;
    if (sid == null || authority !== AUTH.MASTER) return;
    setMileageToggleLoading(true);
    try {
      await updateStoreMileageDisplay(sid, checked);
      setCurrentStoreInfo((prev) => ({
        ...prev,
        storeLists:
          prev.storeLists?.map((s) =>
            s.storeId === sid ? { ...s, mileageDisplayYn: checked ? 'Y' : 'N' } : s,
          ) ?? [],
      }));
      // 마일리지 표시 여부에 따라 API 집계(CASH 포함/제외)가 달라지므로 대시보드 요약 재조회
      await queryClient.invalidateQueries({ queryKey: [SALESDASHBOARD.DASHBOARDSUMMARY] });
    } catch (e) {
      console.error('마일리지 표시 설정 실패', e);
    } finally {
      setMileageToggleLoading(false);
    }
  };

  return (
    <>
      <div className="dashboard_header">
        <div onClick={handleShow}>
          <img className="hamburger_btn" src={hamburgerIcon} alt="햄버거 버튼" />
        </div>

        <div className="iot_cctv_wrapper">
          {authority === AUTH.MASTER && <ExcelRead />}
          {authority !== AUTH.USER && (
            <>
              <Button className="cctv_btn_wrapper" onClick={cctvSeparationHandler}>
                <img src={cctvIcon} alt="cctv 아이콘" className="icon" />
                <div className="text">CCTV</div>
              </Button>
              <Button className="cctv_btn_wrapper" onClick={() => navigation('/admin/hejHome')}>
                <img src={iotIcon} alt="cctv 아이콘" className="icon" />
                <div className="text">IOT</div>
              </Button>
            </>
          )}

          <OverlayTrigger
            trigger="click"
            placement="bottom"
            rootClose={true}
            overlay={
              <Popover id={`popover-positioned-bottom`} className="custom_popover">
                <Popover.Body>
                  <div className="go_user_info_nav" onClick={() => navigation('/admin/mypage')}>
                    {/* <img className="nav_icon" alt="로그아웃 아이콘" src={logoutIcon} /> */}
                    <div className="title">마이페이지</div>
                  </div>
                  <div className="login_nav" onClick={logoutHandler}>
                    {/* <img className="nav_icon" alt="로그아웃 아이콘" src={logoutIcon} /> */}
                    <div className="title">로그아웃</div>
                  </div>
                </Popover.Body>
              </Popover>
            }
          >
            <div className="login_btn">
              <img className="login_icon" src={loginUserIcon} alt="사용자 아이콘" />
            </div>
          </OverlayTrigger>
        </div>
      </div>

      {!displayNone && (
        <>
          <div className="header_section">
            <div className="custom_dropdown" data-tour="store-picker">
              <StorePickButton
                label={currentStoreInfo?.currentStore || ''}
                selectedId={currentStoreInfo.currentStoreId}
                stores={(currentStoreInfo.storeLists || []).map((list) => ({
                  storeId: list.storeId,
                  storeName: list.storeName,
                }))}
                onQueryChange={
                  authority === AUTH.MASTER
                    ? (value) =>
                        searchStoreHandler({ target: { value } } as ChangeEvent<HTMLInputElement>)
                    : undefined
                }
                extra={
                  authority !== AUTH.MASTER ? (
                    <span className="store_auth">{formattedAuth()}</span>
                  ) : null
                }
                onSelect={(storeId) => changeIndexHandler(storeId)}
              />
            </div>
            {authority === AUTH.MASTER && currentStoreInfo.currentStoreId != null && (
              <Form.Group className="master_mileage_display_toggle">
                <Form.Check
                  type="switch"
                  id="master_mileage_display"
                  checked={mileageVisibleForCurrent}
                  disabled={mileageToggleLoading}
                  onChange={(e) => handleMileageDisplayToggle(e.target.checked)}
                  label="이 지점 마일리지 표시"
                />
              </Form.Group>
            )}
          </div>

          <DashBoardNav />
        </>
      )}
      <Offcanvas show={show} onHide={handleClose}>
        <Offcanvas.Body id="offcanvas_container">
          <DashBoardLeftNavBar />
        </Offcanvas.Body>
      </Offcanvas>
    </>
  );
};

export default DashBoardHeader;
